import {
    FC,
    PropsWithChildren,
    RefObject,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    FadeInAnimationVariantType,
    useRuntimeConfig,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { usePersonalDetailsVerificationConfig } from "hooks";
import { RouterQuery } from "utils/constants";
import { ConfigContextV2 } from "./config-v2.provider";

export type IModalForwardRefPropsV2 = {
    open: (animationVariant?: FadeInAnimationVariantType) => void;
    close: () => Promise<void>;
    setStyle?: (style: any) => void;
    isOpened: boolean;
};

interface IModalsContextV2 {
    currentModal: string | null;
    previousModal: string | null;
    setCurrentModal: (val: string | null) => void;
    clearPrevModal: () => void;
    clearCurrentModal: () => void;
    isAnyModalOpened: boolean;
    loginModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    signupModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    accountModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    walletModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    verificationModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    kycVerifyModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    openWalletModal: () => void;
    openKycVerifyModal: () => void;
    personalDetailsVerificationModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    betSlipModalRef: RefObject<IModalForwardRefPropsV2 | null>;
    advancedSidebarModalRef: RefObject<IModalForwardRefPropsV2 | null>;
}

export const ModalsContextV2 = createContext<IModalsContextV2>(null as any);

export const ModalsProviderV2: FC<PropsWithChildren> = ({ children }) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { query } = useSearchParamsQuery();

    const { loadSiteConfig } = useContext(ConfigContextV2);

    const [current, setCurrent] = useState<string | null>(null);
    const [prev, setPrev] = useState<string | null>(null);

    const loginModalRef = useRef<IModalForwardRefPropsV2 | null>(null);
    const signupModalRef = useRef<IModalForwardRefPropsV2 | null>(null);
    const accountModalRef = useRef<IModalForwardRefPropsV2 | null>(null);
    const walletModalRef = useRef<IModalForwardRefPropsV2 | null>(null);
    const verificationModalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);
    const kycVerifyModalRef = useRef<IModalForwardRefPropsV2 | null>(null);
    const personalDetailsVerificationModalRef = useRef<IModalForwardRefPropsV2 | null>(null);
    const betSlipModalRef = useRef<IModalForwardRefPropsV2 | null>(null);
    const advancedSidebarModalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);
    const { needToVerify } = usePersonalDetailsVerificationConfig();

    useEffect(() => {
        const modalType = query.type;
        if (modalType === RouterQuery.Signup && signupModalRef.current) {
            signupModalRef.current.open();
        } else if (modalType === RouterQuery.Wallet && walletModalRef.current) {
            walletModalRef.current.open();
        } else if (modalType === RouterQuery.Account && accountModalRef.current) {
            accountModalRef.current?.open();
        }
    }, [query.type]);

    const openWalletModal = useCallback(() => {
        if (COMMON_SITE_CONFIGS.verification.enable && needToVerify) {
            personalDetailsVerificationModalRef.current?.open();
        } else {
            void loadSiteConfig();
            walletModalRef.current?.open();
        }
    }, [COMMON_SITE_CONFIGS.verification.enable, loadSiteConfig, needToVerify]);

    const openKycVerifyModal = useCallback(() => {
        kycVerifyModalRef.current?.open();
    }, []);

    const setCurrentModal = useCallback(
        (val: string | null) => {
            setPrev(current);
            setCurrent(val);
        },
        [current],
    );

    const clearCurrentModal = useCallback(() => {
        setCurrent(null);
    }, []);

    const clearPrevModal = useCallback(() => {
        setPrev(null);
    }, []);

    const isAnyModalOpened = useMemo(() => !!(current || prev), [current, prev]);

    const value = useMemo(
        () => ({
            currentModal: current,
            previousModal: prev,
            setCurrentModal,
            clearPrevModal,
            clearCurrentModal,
            isAnyModalOpened,
            loginModalRef,
            signupModalRef,
            accountModalRef,
            walletModalRef,
            verificationModalRef,
            kycVerifyModalRef,
            openWalletModal,
            openKycVerifyModal,
            personalDetailsVerificationModalRef,
            betSlipModalRef,
            advancedSidebarModalRef,
        }),
        [
            clearCurrentModal,
            clearPrevModal,
            current,
            isAnyModalOpened,
            openKycVerifyModal,
            openWalletModal,
            prev,
            setCurrentModal,
            betSlipModalRef,
            advancedSidebarModalRef,
        ],
    );

    return <ModalsContextV2.Provider value={value}>{children}</ModalsContextV2.Provider>;
};
